import type { ReactNode } from "react";
import { Inbox } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { Button } from "@/components/ui/button";

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

/**
 * Centered empty-state block — use when a list or table has no rows.
 *
 * @example
 * <EmptyState
 *   title="Chưa có đơn hàng"
 *   description="Tạo đơn hàng mới từ màn hình POS."
 *   action={{ label: "Mở POS", onClick: () => navigate("/pos") }}
 * />
 */
export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 py-16 text-center",
        className,
      )}
    >
      <div className="text-muted-foreground">
        {icon ?? <Inbox size={48} strokeWidth={1.2} />}
      </div>
      <p className="text-base font-medium">{title}</p>
      {description && (
        <p className="max-w-xs text-sm text-muted-foreground">{description}</p>
      )}
      {action && (
        <Button size="sm" onClick={action.onClick} className="mt-2">
          {action.label}
        </Button>
      )}
    </div>
  );
}
